import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationItems = [
    { name: 'Home', path: '/', icon: 'Home' },
    { name: 'Rechtsgebiete', path: '/legal-areas-landing', icon: 'Scale' },
    { name: 'Für Rechtsanwälte', path: '/for-lawyers', icon: 'Users' },
    { name: 'Über uns', path: '/about-us', icon: 'Info' },
  ];

  const moreMenuItems = [
    { name: 'Contact', path: '/contact', icon: 'Phone' },
    { name: 'Trust Center', path: '/trust-center', icon: 'Shield' },
    { name: 'Legal Compliance', path: '/legal-compliance', icon: 'FileText' },
    { name: 'Impressum', path: '/impressum', icon: 'FileText' },
    { name: 'Datenschutzerklärung', path: '/datenschutz', icon: 'Shield' },
  ];

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-professional border-b border-slate-200' 
            : 'bg-white'
        }`}
      >
        <div className="w-full">
          <div className="flex items-center justify-between h-18 px-4 sm:px-6 lg:px-8" style={{height: '72px'}}>
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center focus-ring rounded-lg"
            >
              <img
                src="/assets/images/Logo-1788636880207.png"
                alt="AdvoCall Rechtsanwaltsgesellschaft Logo"
                className="h-14 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-2 ml-12">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 focus-ring ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-text-secondary hover:text-primary hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={16} />
                  <span>{item?.name}</span>
                </Link>
              ))}
              
              {/* More Menu */}
              <div className="relative group">
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium text-text-secondary hover:text-primary hover:bg-muted transition-all duration-200 focus-ring">
                  <Icon name="MoreHorizontal" size={16} />
                  <span>More</span>
                </button>
                
                <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-elevated border border-slate-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="py-2">
                    {moreMenuItems?.map((item) => (
                      item?.external ? (
                        <a
                          key={item?.path}
                          href={item?.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-3 px-4 py-2 text-sm transition-colors duration-200 text-text-secondary hover:text-primary hover:bg-muted"
                        >
                          <Icon name={item?.icon} size={16} />
                          <span>{item?.name}</span>
                        </a>
                      ) : (
                        <Link
                          key={item?.path}
                          to={item?.path}
                          className={`flex items-center space-x-3 px-4 py-2 text-sm transition-colors duration-200 ${
                            isActivePath(item?.path)
                              ? 'bg-primary text-white' :'text-text-secondary hover:text-primary hover:bg-muted'
                          }`}
                        >
                          <Icon name={item?.icon} size={16} />
                          <span>{item?.name}</span>
                        </Link>
                      )
                    ))}
                  </div>
                </div>
              </div>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden sm:flex items-center space-x-3">
              <a href="tel:09001000312">
                <Button
                  variant="default"
                  size="sm"
                  iconName="Phone"
                  iconPosition="left"
                  className="cta-button bg-primary hover:bg-blue-900 text-white"
                >
                  Jetzt anrufen - 09001000312
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-lg text-text-secondary hover:text-primary hover:bg-muted transition-colors duration-200 focus-ring"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={24} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-slate-200 shadow-elevated">
            <div className="px-4 py-4 space-y-2">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 touch-target ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-white' :'text-text-secondary hover:text-primary hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.name}</span>
                </Link>
              ))}
              
              <div className="border-t border-slate-200 pt-2 mt-2">
                {moreMenuItems?.map((item) => (
                  item?.external ? (
                    <a
                      key={item?.path}
                      href={item?.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 touch-target text-text-secondary hover:text-primary hover:bg-muted"
                    >
                      <Icon name={item?.icon} size={18} />
                      <span>{item?.name}</span>
                    </a>
                  ) : (
                    <Link
                      key={item?.path}
                      to={item?.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 touch-target ${
                        isActivePath(item?.path)
                          ? 'bg-primary text-white' :'text-text-secondary hover:text-primary hover:bg-muted'
                      }`}
                    >
                      <Icon name={item?.icon} size={18} />
                      <span>{item?.name}</span>
                    </Link>
                  )
                ))}
              </div>
              
              <div className="border-t border-slate-200 pt-4 mt-4 space-y-3">
                <a href="tel:09001000312">
                  <Button
                    variant="default"
                    fullWidth
                    iconName="Phone"
                    iconPosition="left"
                    className="bg-primary hover:bg-blue-900 text-white"
                  >
                    Jetzt anrufen - 09001000312
                  </Button>
                </a>
              </div>
            </div>
          </div>
        )}
      </header>
      {/* Floating Contact Button */}
      <div className="floating-contact">
        <a href="tel:09001000312">
          <Button
            variant="default"
            size="lg"
            iconName="Phone"
            className="pulse-gentle bg-primary hover:bg-blue-900 text-white shadow-floating rounded-full w-14 h-14 p-0"
            aria-label="Jetzt anrufen - 09001000312"
          />
        </a>
      </div>
      {/* Header Spacer */}
      <div className="h-18" style={{height: '72px'}} />
    </>
  );
};

export default Header;